import theme from '../../theme';
import { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { CLERK_CONFIGURED } from '../../lib/clerkConfig';
import { MAIN_SITE_URL } from '../../lib/subdomain';
import { fetchReviewerStatus } from '../../lib/capstoneApi';
// Same reasoning as the main NavBar's auth widget: kept as its own
// component so useAuth() only gets called once Clerk is actually mounted
// (this bar itself renders unconditionally, even before Clerk loads).
import { useAuth, SignInButton, UserButton } from '@clerk/react';

// Shows the Review link only to reviewers. This is a convenience, not a
// control: the backend authorizes every review route independently, so a
// non-reviewer typing /review sees an empty, erroring page either way
// (docs/05_SECURITY §2, and ADR-0005).
function ReviewNavLink({ onNavigate }) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const [isReviewer, setIsReviewer] = useState(false);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      setIsReviewer(false);
      return undefined;
    }
    let active = true;
    fetchReviewerStatus(getToken)
      .then((res) => active && setIsReviewer(Boolean(res.isReviewer)))
      .catch(() => active && setIsReviewer(false));
    return () => { active = false; };
  }, [isLoaded, isSignedIn, getToken]);

  if (!isReviewer) return null;
  return (
    <NavLink to="/review" className="learnnav-link" onClick={onNavigate}>
      <span>Review</span>
    </NavLink>
  );
}

function LearnAuthWidget() {
  const { isLoaded, isSignedIn } = useAuth();
  if (!isLoaded) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginLeft: '4px' }}>
      {isSignedIn ? (
        <UserButton afterSignOutUrl="/" />
      ) : (
        <SignInButton mode="modal">
          <button type="button" className="learnnav-link" style={{ background: 'none', border: 'none', cursor: 'pointer', font: 'inherit' }}>
            <span>Sign In</span>
          </button>
        </SignInButton>
      )}
    </div>
  );
}

const CSS = `
  .learnnav-root {
    background: ${theme.bgDark};
    height: 52px;
    padding: 0 40px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    position: sticky;
    top: 0;
    z-index: 100;
    flex-shrink: 0;
    border-bottom: 1px solid rgba(var(--ap-gold-rgb), 0.2);
  }
  .learnnav-header { display: flex; align-items: center; gap: 16px; min-width: 0; }
  .learnnav-logo {
    font-family: 'EB Garamond', serif;
    font-size: 17px;
    color: ${theme.textPrimary};
    text-decoration: none;
    letter-spacing: 0.02em;
    white-space: nowrap;
  }
  .learnnav-logo:hover { color: ${theme.gold}; }
  .learnnav-back {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 11px;
    letter-spacing: 0.05em;
    color: ${theme.textMuted};
    text-decoration: none;
    white-space: nowrap;
  }
  .learnnav-back:hover { color: ${theme.gold}; }
  .learnnav-nav { display: flex; gap: 22px; align-items: center; }
  .learnnav-link {
    color: ${theme.textMuted};
    text-decoration: none;
    font-size: 12px;
    font-family: 'Josefin Sans', sans-serif;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    white-space: nowrap;
  }
  .learnnav-link:hover, .learnnav-link.active {
    color: ${theme.gold};
    text-decoration: underline;
    text-underline-offset: 4px;
  }
  .learnnav-toggle { display: none; }
  @media (max-width: 700px) {
    .learnnav-root { padding: 0 20px; }
    .learnnav-back { display: none; }
    .learnnav-toggle {
      display: flex;
      width: 40px; height: 40px;
      background: transparent; border: none; cursor: pointer;
      align-items: center; justify-content: center;
      flex-direction: column; gap: 4px;
    }
    .learnnav-toggle span { display: block; width: 20px; height: 1.5px; background: ${theme.gold}; }
    .learnnav-nav {
      position: fixed;
      top: 52px; left: 0; width: 100%;
      height: calc(100vh - 52px);
      background: rgba(var(--ap-bg-dark-rgb), 0.98);
      backdrop-filter: blur(12px);
      flex-direction: column;
      justify-content: center;
      transform: translateY(-100%);
      opacity: 0;
      pointer-events: none;
      transition: transform 0.3s ease, opacity 0.3s ease;
    }
    .learnnav-nav.open { transform: translateY(0); opacity: 1; pointer-events: auto; }
    .learnnav-link { font-size: 0.9rem !important; letter-spacing: 0.2em !important; }
  }
`;

function LearnNavBar() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const closeMenu = () => setIsOpen(false);

  return (
    <nav className="learnnav-root">
      <style>{CSS}</style>
      <div className="learnnav-header">
        <Link to="/" className="learnnav-logo" onClick={closeMenu}>Austrian Process - Learn</Link>
        <a href={MAIN_SITE_URL} className="learnnav-back">&larr; austrianprocess.com</a>
      </div>
      <button type="button" className="learnnav-toggle" onClick={() => setIsOpen((o) => !o)} aria-label="Toggle menu">
        <span /><span /><span />
      </button>
      <div className={`learnnav-nav ${isOpen ? 'open' : ''}`}>
        <NavLink to="/" end className="learnnav-link" onClick={closeMenu}><span>Courses</span></NavLink>
        <NavLink to="/my-learning" className="learnnav-link" onClick={closeMenu}><span>My Learning</span></NavLink>
        <NavLink to="/readings" className="learnnav-link" onClick={closeMenu}><span>Readings</span></NavLink>
        <NavLink to="/capstone" className="learnnav-link" onClick={closeMenu}><span>Capstone</span></NavLink>
        <NavLink to="/certificate" className="learnnav-link" onClick={closeMenu}><span>Certificate</span></NavLink>
        {CLERK_CONFIGURED && <ReviewNavLink onNavigate={closeMenu} />}
        {CLERK_CONFIGURED && <LearnAuthWidget />}
      </div>
    </nav>
  );
}

export default LearnNavBar;
